import { inject, Injectable } from '@angular/core';
import { Engagement } from '../data-models/engagement';
import { map } from 'rxjs/operators';
import { EngagementRespondent } from '../data-models/engagement-respondent';
import { EngagementMetric } from '../data-models/engagement-metric';
import { Respondent } from '../data-models/respondent';
import { Questionnaire } from '../data-models/questionnaire';
import { take } from 'rxjs/operators';
import { RespondentReference } from '../data-models/respondent-reference';
import { EngagementEmailTemplate } from '../data-models/engagement-email-template';
import { EngagementReference } from '../data-models/engagement-reference';
import { LeaverAnalysis } from '../data-models/leaver-analysis';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TenantEngagementData } from '../data-models/tenant-engagement-reference';
import { EngagementAnalysis } from '../data-models/engagement-analysis';
import { EngagementAnswer } from '../data-models/engagement-answer';
import { EngagementComment } from '../data-models/engagement-comment';
import { EngagementAnalysisTemplate } from '../data-models/engagement-analysis-template';
import { GlobalsService } from './globals.service';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { EnpsAnalysisRequest, EnpsAnalysisResponse } from './engagement.service';
import { Database, equalTo, get, off, onValue, orderByChild, push, query, ref, remove, set, update } from '@angular/fire/database';
import { Auth } from '@angular/fire/auth';
import { PushidService } from './pushid.service';

@Injectable({
  providedIn: 'root'
})
export class HappinessService {

  sEndpoint: string = 'https://apis.zenithr.net/api/';
  sDevEndpoint: string = 'http://localhost:52619/api/';
  fbFunctionURL: string = '';

  pushIdService = inject(PushidService);

  constructor(private db: Database, public afAuth: Auth, private http: HttpClient, private globals: GlobalsService) {
    this.sEndpoint = this.globals.sEndpoint;
    this.fbFunctionURL = this.globals.fbFunctionURL;

  }


  // Update Engagement
  updateEngagement(tenantKey: string, companyKey: string, engagement: Engagement) {
    const engagementRef = ref(this.db, `/TenantData/${tenantKey}/Happiness/${companyKey}/${engagement.key}`);
    return update(engagementRef, engagement);
  }

  // Fetch Engagements Draft (once)
  fetchEngagementsDraft(tenantKey: string, companyKey: string) {
    const engagementsRef = query(
      ref(this.db, `/TenantData/${tenantKey}/Happiness/${companyKey}`),
      orderByChild('status'),
      equalTo('DRAFT')
    );

    return from(get(engagementsRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  removeLocalAnswers(request) {
    return this.http.post(this.fbFunctionURL + 'removeLocalHappinessAnswers', request);
  }

  bulkAddRespondents(request) {
    return this.http.post(this.fbFunctionURL + 'bulkAddHappinessRespondents', request);
  }
  fetchPoolRespondent(request) {
    return this.http.post(this.sEndpoint + 'Respondent/FetchPoolRespondent', request);

  }
  bulkUpdateEngagementSimulationAnswers(request) {
    return this.http.post(this.fbFunctionURL + 'bulkUpdateHappinessSimulationAnswers', request);
  }

  createEngagementResList(request) {
    return this.http.post(this.sEndpoint + 'Happiness/CreateEngagementResList', request);
  }

  sendEngagementReminders(request) {
    return this.http.post(this.sEndpoint + 'Happiness/SendHappinessReminders', request);
  }

  fetchIndexAndQuestionAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Happiness/IndexAndQuestionAnalysis', request);
  }

  getTreeList(request) {
    return this.http.post(this.sEndpoint + 'Happiness/GetEntityTreeList', request);
  }

  getAppliedEntity(request) {
    return this.http.post(this.sEndpoint + 'Happiness/GetAppliedEntity', request);
  }

  GetEngagementCommentsForSection(request) {
    return this.http.post(this.sEndpoint + 'Happiness/GetHappinessCommentsForSection', request);
  }

  fetchPreviousScores(request) {
    return this.http.post(this.sEndpoint + 'Happiness/GetPreviousData', request);
  }

  GetEngagementCommentsForQuestion(request) {
    return this.http.post(this.sEndpoint + 'Happiness/GetHappinessCommentsForQuestion', request);
  }

  populateResponseCharts(request) {
    return this.http.post(this.sEndpoint + 'Happiness/PopulateResponseCharts', request);
  }

  populateResponseCriteriaChart(request) {
    return this.http.post(this.sEndpoint + 'Happiness/PopulateResponseCriteriaChart', request);
  }

  GetAllEngagementCommentsForSection(request) {
    return this.http.post(this.sEndpoint + 'Happiness/GetAllHappinessCommentsForSection', request);
  }

  populateResponseCriteria(request) {
    return this.http.post(this.sEndpoint + 'Happiness/PopulateResponseCriteria', request);
  }

  updateRespondentDetails(request) {
    return this.http.post(this.sEndpoint + 'Happiness/UpdateRespondent', request);
  }

  // Fetch Engagement Respondent List (once)
  fetchEngagementRespondentListOnce(tenantKey: string, companyKey: string, engagementKey: string) {
    const respondentsRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList`);
    return from(get(respondentsRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  fetchEngagements(tenantKey: string, companyKey: string): Observable<any[]> {
    const engagementsRef = query(
      ref(this.db, `/TenantData/${tenantKey}/Happiness/${companyKey}`),
      orderByChild('isDeleted'),
      equalTo(false)
    );

    return new Observable<any[]>(subscriber => {
      // Start listening to changes
      const listener = onValue(engagementsRef, snapshot => {
        const data = snapshot.val() || {};
        const results = Object.keys(data).map(key => ({ key, ...data[key] }));
        subscriber.next(results);
      }, error => subscriber.error(error));

      // Cleanup listener when unsubscribed
      return () => off(engagementsRef, 'value', listener);
    });
  }

  // Fetch single Engagement
  fetchEngagement(tenantKey: string, companyKey: string, engagementKey: string) {
    const engagementRef = ref(this.db, `/TenantData/${tenantKey}/Happiness/${companyKey}/${engagementKey}`);
    return from(get(engagementRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return { key: engagementKey, ...data };
      }),
      take(1)
    );
  }

  insertEngagementTemplate(request) {
    return this.http.post(this.sEndpoint + 'SurveyDetails/SurveyGoLive', request);
  }


  insertSurveyAnswers(request) {
    return this.http.post(this.sEndpoint + 'Happiness/SubmitAnswers', request);
  }

  updateFinalSurvey(request) {
    return this.http.post(this.sEndpoint + 'UpdateSurveyDetails', request);
  }


  // Fetch Engagement Respondents (live)
  fetchEngagementRespondentList(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      })
    );
  }

  // Fetch single Engagement Respondent (once)
  fetchEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList/${respondentKey}`);
    return from(get(itemRef)).pipe(
      map(snapshot => ({ key: respondentKey, ...snapshot.val() })),
      take(1)
    );
  }

  // Fetch all respondents (live)
  fetchRespondents(tenantKey: string, companyKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      })
    );
  }

  // Soft remove engagement respondent
  removeEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const statusRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList/${respondentKey}/status`);
    const actualStatusRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList/${respondentKey}/actualStatus`);
    return Promise.all([set(statusRef, 'DELETED'), set(actualStatusRef, 'DELETED')]);
  }

  // Hard delete engagement respondent
  deleteEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList/${respondentKey}`);
    return remove(itemRef);
  }

  // Fetch Engagement Metrics (live)
  fetchEngagementMetrics(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentMetrics`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      })
    );
  }

  fetchSurveySnapshot(request) {
    return this.http.post(this.sEndpoint + 'Happiness/FetchSurveySnapshot', request);
  }


  // fetchEngagementComparisonData(request) {
  //   return this.http.post(this.fbFunctionURL + 'fetchTenantEngagements', request);

  // }

  // Update closed engagement data
  updateClosedEngagementData(
    tenantKey: string,
    companyKey: string,
    companyName: any[],
    engagement: Engagement,
    engagementAnalysis: EngagementAnalysis
  ) {
    let tenantEngagementData = new TenantEngagementData();
    tenantEngagementData.key = engagement.key;
    tenantEngagementData.title = engagement.title;
    tenantEngagementData.description = engagement.description;
    tenantEngagementData.unixStart = engagement.unixStart;
    tenantEngagementData.companyKey = companyKey;
    tenantEngagementData.workplaceEngagementScore = engagementAnalysis.workPlaceEngagementScore;
    tenantEngagementData.companyName = companyName;

    for (let engDriver of engagementAnalysis.engagementDrivers) {
      let driverName = engDriver.name.find(n => n.key === 'EN');
      tenantEngagementData.engagementDrivers.push({
        key: driverName.name,
        name: engDriver.name,
        score: engDriver.meanScore
      });
    }

    tenantEngagementData.lowlyEngaged = engagementAnalysis.lowlyEngaged;
    tenantEngagementData.moderatlyEngaged = engagementAnalysis.moderatlyEngaged;
    tenantEngagementData.highlyEngaged = engagementAnalysis.highlyEngaged;

    const itemRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDataAnalysis/${tenantEngagementData.key}`);
    return update(itemRef, tenantEngagementData);
  }

  // Fetch predefined metrics (once)
  fetchPredefinedMetrics(tenantKey: string, companyKey: string, respondentKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/RespondentDetails/${companyKey}/${respondentKey}`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  // Fetch respondent from pool by email (once)
  fetchRespondentFromPool(tenantKey: string, companyKey: string, respondentEmail: string) {
    const listRef = query(
      ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}`),
      orderByChild('email'),
      equalTo(respondentEmail)
    );
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  // Update predefined metrics
  updatePredefinedMetrics(tenantKey: string, companyKey: string, respondentKey: string, metrics: any[]) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/RespondentDetails/${companyKey}/${respondentKey}`);
    return set(itemRef, metrics);
  }

  updateEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, engagementRespondents: EngagementRespondent[], userKey?, sendEmail: boolean = false, companyName?: any[], engagement?: Engagement) {
    for (let i = 0; i < engagementRespondents.length; i++) {
      let engagementRespondent: EngagementRespondent = engagementRespondents[i];
      if (!engagementRespondent.key) {
        engagementRespondent.key = this.pushIdService.generatePushId();
        engagementRespondent.referenceKey = this.pushIdService.generatePushId();
        engagementRespondent.companyKey = companyKey;
        engagementRespondent.tenantKey = tenantKey;
      }
      engagementRespondent.createdBy = 'SELF';
      engagementRespondent.referenceKey = engagementRespondent.referenceKey;
    }
    let request = {
      SurveyType: 'HAP',
      TenantKey: tenantKey,
      CompanyKey: companyKey,
      SurveyKey: engagementKey,
      respondents: engagementRespondents,
      sendEmail: sendEmail,
      CompanyName: companyName,
      surveyInfo: engagement
    }
    return this.http.post(this.sEndpoint + 'Respondent/UpdateSurveyRespondent', request);
  }

  // Update engagement reference
  updateEngagementReference(respondentReference: RespondentReference) {
    const itemRef = ref(this.db, `/HappinessReferences/${respondentReference.key}`);
    return update(itemRef, respondentReference);
  }

  // Update engagement metrics
  updateEngagementMetrics(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    metrics: EngagementMetric[]
  ) {
    // Generate push IDs for metrics that don't have a key
    metrics.forEach(metric => {
      if (!metric.key) {
        metric.key = push(ref(this.db)).key!;
      }
    });

    const itemRef = ref(
      this.db,
      `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentMetrics/`
    );
    return set(itemRef, metrics);
  }

  // Update respondent in pool
  updateRespondentList(tenantKey: string, companyKey: string, respondent: Respondent) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}/${respondent.key}`);
    return update(itemRef, respondent)
  }

  // Remove respondent from pool
  removeRespondentFromPool(tenantKey: string, companyKey: string, respondentKey: string) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}/${respondentKey}`);
    return remove(itemRef);
  }

  // Fetch engagement questionnaires (once)
  fetchEngagementQuestionnaire() {
    const listRef = query(ref(this.db, '/Questionnaires/'), orderByChild('isHappiness'), equalTo(true));
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  // Update engagement respondent survey
  updateEngagementRespondentSurvey(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    analysis: LeaverAnalysis
  ) {
    const itemRef = ref(this.db, `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/${analysis.key}`);
    return update(itemRef, analysis);
  }

  // Fetch Engagement Templates (once)
  fetchEngagementTemplates(tenantKey: string, companyKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/HappinessTemplates/${companyKey}/`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  // Fetch single Engagement Reference
  fetchEngagementReference(engagementReference: string) {
    const itemRef = ref(this.db, `/HappinessReferences/${engagementReference}`);
    return from(get(itemRef)).pipe(
      map(snapshot => ({ key: snapshot.key, ...snapshot.val() })),
      take(1)
    );
  }

  // Soft delete Engagement
  removeEngagement(tenantKey: string, companyKey: string, engagementKey: string) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/Happiness/${companyKey}/${engagementKey}/isDeleted`);
    const softDelete = set(itemRef, true);

    // Dual-delete SQL comparison snapshot (Phase 1)
    this.http.post(this.sEndpoint + 'Comparison/Remove', {
      TenantKey: tenantKey,
      CompanyKey: companyKey,
      SurveyKey: engagementKey,
      SurveyType: 'HAP'
    }).subscribe({ error: () => { } });

    return softDelete;
  }

  // Remove all respondents for an engagement
  removeEngagementRespondentList(tenantKey: string, companyKey: string, engagementKey: string) {
    const itemRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}`);
    return remove(itemRef);
  }

  // Remove a specific engagement respondent's analysis
  removeEngagementRespondentAnalysis(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const itemRef = ref(this.db, `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/${respondentKey}`);
    return remove(itemRef);
  }

  // Fetch Engagement Analysis (once)
  fetchEngagementAnalysis(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  checkBalanceAndCreditEngagement(request) {
    return this.http.post(this.fbFunctionURL + 'creditHappiness', request);
  }

  fetchBenchmark(request) {
    return this.http.post(this.fbFunctionURL + 'fetchAllHappinessBenchmark', request);
  }

  fetchEngagementComparisonData(request) {
    return this.http.post(this.fbFunctionURL + 'fetchTenantHappiness', request);

  }

  // Insert or update an engagement answer
  insertEngagementAnswers(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    answerKey: string,
    engagementAnswer: EngagementAnswer
  ) {
    const answerRef = ref(
      this.db,
      `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Answers/${answerKey}`
    );
    return update(answerRef, engagementAnswer);
  }

  // Insert or update an engagement comment
  insertEngagementComment(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    answerKey: string,
    engagementComment: EngagementComment
  ) {
    const commentRef = ref(
      this.db,
      `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Comments/${answerKey}`
    );
    return update(commentRef, engagementComment);
  }

  engagementGoLive(request) {
    return this.http.post(this.fbFunctionURL + 'happinessGoLive', request);
  }

  // Insert or update engagement analysis template
  insertEngagementAnalysisTemplate(engagementAnalysisTemplate: EngagementAnalysisTemplate) {
    const objRef = ref(this.db, `/HappinessAnalysis/${engagementAnalysisTemplate.tenantKey}/${engagementAnalysisTemplate.companyKey}/${engagementAnalysisTemplate.engagementKey}`);
    return update(objRef, engagementAnalysisTemplate);
  }

  // Force remove all respondents for an engagement
  forceRemoveRespondents(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/HappinessDetails/${companyKey}/${engagementKey}/HappinessRespondentList/`);
    return remove(listRef);
  }

  // Force delete all answers for an engagement
  forceDeleteAnswers(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Answers/`);
    return remove(listRef);
  }
  fetchEngagementQuestionnaireObject() {
    return this.http.post(this.fbFunctionURL + 'fetchQuestionnaire', { questionnaireType: 'HAPPINESS' });
  }

  fetchEngagementAnswersOnce(request) {
    return this.http.post(this.fbFunctionURL + 'fetchHappinessAnswers', request);
  }

  // Fetch Engagement Comments (once)
  fetchEngagementComments(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/HappinessAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Comments/`);
    return from(get(listRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  // Fetch Engagements (once) where isDeleted == false
  fetchEngagementsOnce(tenantKey: string, companyKey: string) {
    const listQuery = query(
      ref(this.db, `/TenantData/${tenantKey}/Happiness/${companyKey}/`),
      orderByChild('isDeleted'),
      equalTo(false)
    );
    return from(get(listQuery)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.keys(data).map(key => ({ key, ...data[key] }));
      }),
      take(1)
    );
  }

  fetchEngagementCommentsFromSDB(request) {
    return this.http.post(this.fbFunctionURL + 'fetchCommentsFromSDB', request);
  }

  fetchEngagementAnswersSDB(request) {
    return this.http.post(this.sEndpoint + 'Happiness/ClosedEngagement', request);
  }

  fetchRespondentsFromSDB(request) {
    return this.http.post(this.fbFunctionURL + 'fetchRespondentsFromDB', request);
  }

  updateEngagementRespondentInDB(request) {
    return this.http.put(this.fbFunctionURL + 'updateEngagementRespondentInDB', request);
  }

  migrateHappinessAnswers(rAnalysis: any, engagementKey) {
    let request = { rAnalysis: rAnalysis, engagementKey: engagementKey };
    return this.http.post('http://localhost:5001/exitanalysis-a6325/us-central1/migrateHappinessAnswers', request);
  }

  forceSetHappinessTemplate(engagementAnalysisTemplate: EngagementAnalysisTemplate) {
    let request = { engagementTemplate: engagementAnalysisTemplate };
    return this.http.post('http://localhost:5001/exitanalysis-a6325/us-central1/forceSetHappinessTemplate', request);
  }

  fetchQuestionAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Happiness/FetchQuestionAISentiment', request);
  }
  fetchEngagementTopics(request) {
    return this.http.post(this.sEndpoint + 'Happiness/FetchTopics', request);
  }
  private analyticsSurvey = new BehaviorSubject<AnalyticsEngagement>(null);
  analyticsSurvey$ = this.analyticsSurvey.asObservable();

  setAnalyticsSurvey(filters: AnalyticsEngagement) {
    this.analyticsSurvey.next(filters);
  }
  updateCommentSentiment(request) {
    return this.http.post(this.sEndpoint + 'Happiness/UpdateCommentSentiment', request);
  }
  selectedCriterias$: BehaviorSubject<EngagementMetric[] | any> = new BehaviorSubject(['']);
  surveyData$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);


  /**
   * Bulk delete respondents in live view for happiness
   * 
   * If IsDeleteAll is True, RespondentIds in the Ids property would be skipped
   * If IsDeleteAll is False, RespondentIds in the Ids property would be used to delete respondents
   * 
   * Ids hold Respondent ID as an array
   * @param request 
   * @returns 
   */
  bulkDeleteRespondents(request: BulkDeleteRequest) {
    return this.http.post(`${this.sEndpoint}Happiness/BulkDeleteRespondents`, request);
  }


  /**
   * Clone Survey 
   * @param request 
   * @returns 
   */
  cloneSurvey(request: CloneSurveyRequest) {
    return this.http.post(`${this.sEndpoint}Happiness/CloneSurvey`, request);
  }
  GetHappinessLiveAnalysis(tenantKey: string, companyKey: string, surveyKey: string, Criteria: string): Observable<any> {
    let params = new HttpParams()
      .set('TenantKey', tenantKey)
      .set('CompanyKey', companyKey)
      .set('SurveyKey', surveyKey)
      .set('Criteria', Criteria);

    return this.http.get<any>(this.sEndpoint + 'Happiness/GetLiveAnalysis', { params });
  }

  GetEnpsAnalysis(criteria, request: EnpsAnalysisRequest): Observable<EnpsAnalysisResponse> {
    return this.http.post<EnpsAnalysisResponse>(`${this.sEndpoint}Happiness/GetEnpsAnalysis/${criteria}`, request);
  }

  getSecureRespondentLink(
    tenantKey: string,
    companyKey: string,
    surveyKey: string,
    email: string,
    employeeId: string
  ): Observable<any> {
    const params = new HttpParams()
      .set('TenantKey', tenantKey)
      .set('CompanyKey', companyKey)
      .set('SurveyKey', surveyKey)
      .set('Email', email)
      .set('EmployeeId', employeeId);

    return this.http.get<any>(this.sEndpoint + 'Happiness/GetSecureRespondentLink', { params });
  }
}


export interface CloneSurveyRequest {
  TenantKey: string,
  CompanyKey: string,
  SurveyKey: string,
  OriginalSurveyKey: string
}
export interface BulkDeleteRequest {
  TenantKey: string,
  CompanyKey: string,
  SurveyKey: string,
  IsDeleteAll: boolean,
  Ids: any[]
}

export class AnalyticsEngagement {
  tenantKey: string;
  companyKey: string;
  questions: any;
  survey: {
    name: string,
    key: string,
  };
  engagementAnalysis: any;
  generalDriver?: any
}
